import React from 'react';
import cn from 'classnames';
import {browserHistory} from 'react-router';
import * as immutable from 'immutable';
import Button from '@material-ui/core/Button';
import './TopicFormHeader.styl';
import TopicFormNameEditor from 'components/admin/topics/TopicFormNameEditor';
import PropTypes from 'prop-types';
import NewFilterWord from 'records/admin/topics/NewFilterWord';
import {cloneTopic, goToTopicsAdminPage, usedProfiles} from 'components/admin/topics/common';
import TopicDeleteConfirmationDialog from 'components/admin/topics/TopicDeleteConfirmationDialog';
import AdminFilterWordProfileActions from 'actions/admin/AdminFilterWordProfileActions';
import CallTrigger, {
  CallConditionAll,
  CallConditionAny,
  CallConditionType,
  CallConditionUser,
  SpeakerCondition,
} from 'records/CallTrigger';
import {UserConditionType} from 'records/UserCondition';
import AdminTopicEditorPageActions from 'actions/admin/AdminTopicEditorPageActions';
import GenericLink from 'components/GenericLink';
import {EditIcon} from '@components/icon/EditIcon';
import {TrashIcon} from '@components/icon/TrashIcon';
import {CloneIcon} from '@components/icon/CloneIcon';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';

/**
 * Redirect to the triggers editor.
 *
 * @param topic {NewFilterWord} the topic we're creating a new trigger for.
 */
function goToSmartAlerts(topic) {
  const conditions = [
    new CallConditionAny({
      matchAny: [
        new CallConditionUser({
          userCondition: UserConditionType.getUserCondition(UserConditionType.ANY_USER),
        }),
      ],
    }),
    new CallConditionAny({
      matchAny: [
        CallConditionType.getCallCondition({
          type: CallConditionType.MENTIONED_AT_LEAST_ONCE,
          speakerType: SpeakerCondition.CALLER,
          keywordTopicPhrase: {filterWordId: topic.filterWordId},
        }),
      ],
    }),
  ];
  const rule = new CallTrigger({
    conditions: new CallConditionAll({matchAll: conditions}),
  });
  const state = {
    rule,
  };
  browserHistory.push({
    pathname: '/admin/actions/create',
    state,
  });
}

/**
 * Produce a message to show in the delete confirmation dialog.
 *
 * @param idToProfileMap {Map<String, NewFilterWordProfile>} map of filter word profile id to
 *  profile.
 * @param profileDepartmentAndTeamUsage {Map<String, List<Object>>} map of filter word profile id
 *  to teams and departments that use that profile.
 * @param topic {NewFilterWord} the topic.
 * @param triggers {List} triggers referring to this topic.
 */
function usageMessage(idToProfileMap, profileDepartmentAndTeamUsage, topic, triggers) {
  const usedInTriggers = !triggers.isEmpty();
  const departments = usedProfiles(topic, idToProfileMap, profileDepartmentAndTeamUsage)
    // Find usages. We know these are not null, because we've already filtered on that.
    .flatMap(profile => profileDepartmentAndTeamUsage.get(profile.filterWordProfileId))
    // Get count of unique department IDs.
    .map(usage => usage.department.id)
    .toSet();
  const usedInDepartments = !departments.isEmpty();

  let triggersUsage = '';
  if (usedInTriggers) {
    const sa = 'Smart Alert';
    const noun = triggers.size === 1 ? sa : `${sa}s`;
    triggersUsage = `${triggers.size} ${noun}`;
  }
  let departmentsUsage = '';
  if (usedInDepartments) {
    const dept = 'Department';
    const noun = departments.size === 1 ? dept : `${dept}s`;
    departmentsUsage = `${departments.size} ${noun}`;
  }
  const suffix =
    usedInTriggers && usedInDepartments
      ? `${triggersUsage} and ${departmentsUsage}`
      : usedInTriggers
      ? triggersUsage
      : departmentsUsage;
  const start = usedInTriggers || usedInDepartments ? `This Topic is used by ${suffix}. ` : '';
  return usedInTriggers ? `${start}Related Smart Alerts will be disabled if you proceed. ` : start;
}

const DISPLAY_WHEN_OPTIONS = [
  {
    label: 'Show In Sidebar',
    value: 'ALWAYS',
  },
  {
    label: 'Show When Found',
    value: 'WHEN_FOUND',
  },
  {
    label: 'Show When Searched',
    value: 'WHEN_SEARCHED',
  },
];

const SPEAKER_MATCH_OPTIONS = [
  {
    label: 'Anyone',
    value: 'ALL',
  },
  {
    label: 'Internal',
    value: 'INTERNAL_ONLY',
  },
  {
    label: 'External',
    value: 'EXTERNAL_ONLY',
  },
];

/**
 * Topic form header bar. The topic name, edit controls, and link to smart alerts live here.
 */
export default class TopicFormHeader extends React.PureComponent {
  static propTypes = {
    editable: PropTypes.bool.isRequired,
    editingNewTopic: PropTypes.bool,
    editingTopicName: PropTypes.bool,
    idToProfileMap: PropTypes.instanceOf(immutable.Map).isRequired,
    profileDepartmentAndTeamUsage: PropTypes.instanceOf(immutable.Map).isRequired,
    topic: PropTypes.instanceOf(NewFilterWord),
    topicName: PropTypes.string,
    triggers: PropTypes.instanceOf(immutable.List).isRequired,
  };

  constructor(props) {
    super(props);
    this.closeDeleteConfirmationDialog = this.closeDeleteConfirmationDialog.bind(this);
    this.updateDisplayWhen = this.updateDisplayWhen.bind(this);
    this.state = {
      deleteConfirmationDialogOpen: false,
    };
  }

  startEditingName() {
    AdminTopicEditorPageActions.openTopicNameEditor();
  }

  cancelEditingName() {
    AdminTopicEditorPageActions.resetTopicNameEditor();
  }

  editName(newName) {
    // TODO validate, display errors somehow.
    AdminTopicEditorPageActions.setTopicName(newName);
  }

  saveTopicName() {
    AdminTopicEditorPageActions.saveTopicName();
    AdminTopicEditorPageActions.resetTopicNameEditor();
  }

  updateDisplayWhen(value) {
    AdminTopicEditorPageActions.saveDisplayWhen(value);
  }

  updateSpeakerMatch(value) {
    AdminTopicEditorPageActions.saveSpeakerMatch(value);
  }

  updateIncludeInInsights(value) {
    AdminTopicEditorPageActions.updateIncludeInInsights(value);
  }

  openDeleteConfirmationDialog = () => {
    this.setState({
      deleteConfirmationDialogOpen: true,
    });
  };

  closeDeleteConfirmationDialog() {
    this.setState({
      deleteConfirmationDialogOpen: false,
    });
  }

  confirmTopicDeletion = async () => {
    const {topic} = this.props;
    if (typeof topic.filterWordId !== 'undefined') {
      await AdminFilterWordProfileActions.deleteFilterWord(topic.filterWordId);
    }

    this.closeDeleteConfirmationDialog();
    goToTopicsAdminPage();
  };

  render() {
    const {
      editable,
      editingNewTopic,
      editingTopicName,
      idToProfileMap,
      profileDepartmentAndTeamUsage,
      topic,
      topicName,
      triggers,
      showErrors,
    } = this.props;

    const {deleteConfirmationDialogOpen} = this.state;
    const confirmation = 'Are you sure you want to delete this Topic?';
    const usage = usageMessage(idToProfileMap, profileDepartmentAndTeamUsage, topic, triggers);
    const confirmationMessage = `${usage}${confirmation}`;
    const showInsightsError = showErrors && topic.includeInInsights === null;
    return (
      <div className="admin-topic-form-header">
        <div className="top-row">
          <div className="header-left-group">
            {!editingTopicName && (
              <div className="link-and-topic-name">
                <GenericLink onClick={goToTopicsAdminPage}>Keyword Topic Profiles</GenericLink> /{' '}
                {topicName}{' '}
              </div>
            )}
            {editable && !editingTopicName && (
              <div className="header-icons">
                <div>
                  <EditIcon onClick={() => this.startEditingName()} tooltip="Edit" />{' '}
                </div>
                <div>
                  <TrashIcon onClick={this.openDeleteConfirmationDialog} tooltip="Delete" />
                </div>
                <div>
                  <CloneIcon onClick={() => cloneTopic(topic)} tooltip="Clone" />
                </div>
              </div>
            )}
            {editable && editingTopicName && (
              <TopicFormNameEditor
                name={topicName}
                onCancel={() => this.cancelEditingName()}
                onChange={value => this.editName(value)}
                onSave={() => this.saveTopicName()}
              />
            )}
          </div>
          {!editingNewTopic && (
            <div className="create-smart-alert">
              <div className="label">New Smart Alert Based on Topic?</div>
              <Button
                variant="contained"
                color="primary"
                className="ev-button"
                onClick={() => goToSmartAlerts(topic)}>
                Create
              </Button>
            </div>
          )}
        </div>
        {editable && (
          <div className="bottom-row">
            <FormControl error={showInsightsError}>
              <div className={cn('label', {error: showInsightsError})}>Insights visibility</div>
              <Select
                style={{minWidth: 180}}
                MenuProps={{
                  getContentAnchorEl: null,
                  anchorOrigin: {vertical: 'bottom', horizontal: 'left'},
                }}
                onChange={e => this.updateIncludeInInsights(e.target.value)}
                value={topic.includeInInsights}>
                <MenuItem value={true}>Include in insights</MenuItem>
                <MenuItem value={false}>Exclude from insights</MenuItem>
              </Select>
            </FormControl>
            <div>
              <div className="label">Call card visibility</div>
              <Select
                style={{minWidth: 180}}
                MenuProps={{
                  getContentAnchorEl: null,
                  anchorOrigin: {vertical: 'bottom', horizontal: 'left'},
                }}
                onChange={e => this.updateDisplayWhen(e.target.value)}
                value={topic.displayWhen}>
                {DISPLAY_WHEN_OPTIONS.map(({label, value}) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </div>
            <div>
              <div className="label">Speaker matching</div>
              <Select
                style={{minWidth: 180}}
                MenuProps={{
                  getContentAnchorEl: null,
                  anchorOrigin: {vertical: 'bottom', horizontal: 'left'},
                }}
                onChange={e => this.updateSpeakerMatch(e.target.value)}
                value={topic.speakerMatch}>
                {SPEAKER_MATCH_OPTIONS.map(({label, value}) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </div>
          </div>
        )}
        <TopicDeleteConfirmationDialog
          message={confirmationMessage}
          onClose={this.closeDeleteConfirmationDialog}
          onConfirm={this.confirmTopicDeletion}
          open={deleteConfirmationDialogOpen}
        />
      </div>
    );
  }
}
